import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import "./FinanceWorkspace.css";

const money = (value) => Number(value || 0).toFixed(2);

const sections = [
  ["billing", "Billing", "bi-receipt"],
  ["receipts", "Receipting", "bi-cash-stack"],
  ["credit_notes", "Credit Notes", "bi-file-earmark-minus"],
  ["payment_reversals", "Payment Reversals", "bi-arrow-counterclockwise"],
  ["commission_payments", "Commission Payments", "bi-cash-coin"],
  ["commission_reversals", "Commission Reversals", "bi-arrow-repeat"],
  ["debit_order_payers", "Debit Order Payers", "bi-bank"],
];

function FinanceWorkspace() {
  const [section, setSection] = useState("billing");
  const [data, setData] = useState({});
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    const response = await axiosInstance.get("finance/", {
      params: {
        section,
        ...(search.trim() ? { search: search.trim() } : {}),
      },
    });
    setData(response.data || {});
  };

  useEffect(() => {
    load().catch(() => toast.error("Could not load Finance & Transactions."));
  }, []);

  const rows = data[section] || [];

  const openCreditNote = (bill) => {
    if (Number(bill.balance) <= 0) {
      toast.info(`Bill ${bill.code} has no outstanding balance.`);
      return;
    }
    setSelected(bill);
    setForm({
      bill: bill.id,
      credit_date: new Date().toISOString().slice(0, 10),
      amount: bill.balance,
      reason: "",
    });
    setModal("credit-note");
  };

  const submitCreditNote = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const response = await axiosInstance.post(
        "finance/credit-notes/",
        form
      );
      toast.success(response.data.detail);
      setModal(null);
      await load();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Credit note could not be effected."
      );
    } finally {
      setBusy(false);
    }
  };

  const reverseReceipt = async (receipt) => {
    const reason = window.prompt(
      `State the exact reason for reversing receipt ${receipt.code}:`
    );
    if (!reason) return;
    setBusy(true);
    try {
      const response = await axiosInstance.post(
        `finance/receipts/${receipt.id}/reverse/`,
        { reason }
      );
      toast.success(response.data.detail);
      await load();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Receipt reversal could not be completed."
      );
    } finally {
      setBusy(false);
    }
  };

  const reverseCommission = async (payment) => {
    const reason = window.prompt(
      `State the exact reason for reversing commission payment ${payment.code}:`
    );
    if (!reason) return;
    setBusy(true);
    try {
      const response = await axiosInstance.post(
        `finance/commission-payments/${payment.id}/reverse/`,
        { reason }
      );
      toast.success(response.data.detail);
      await load();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Commission payment reversal could not be completed."
      );
    } finally {
      setBusy(false);
    }
  };

  const openPayer = (payer = null) => {
    setSelected(payer);
    setForm(
      payer
        ? { ...payer }
        : {
            name: "",
            cellphone: "",
            email: "",
            national_id: "",
            bank_name: "",
            bank_branch: "",
            account_number: "",
            is_active: true,
          }
    );
    setModal("payer");
  };

  const savePayer = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const response = selected
        ? await axiosInstance.patch(
            `finance/debit-order-payers/${selected.id}/`,
            form
          )
        : await axiosInstance.post(
            "finance/debit-order-payers/",
            form
          );
      toast.success(response.data.detail);
      setModal(null);
      await load();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Debit-order payer could not be saved."
      );
    } finally {
      setBusy(false);
    }
  };

  const metrics = useMemo(() => {
    return {
      billBalance: (data.billing || []).reduce(
        (total, item) => total + Number(item.balance || 0),
        0
      ),
      receipts: (data.receipts || [])
        .filter((item) => item.status !== "REVERSED")
        .reduce((total, item) => total + Number(item.amount || 0), 0),
      credits: (data.credit_notes || [])
        .filter((item) => item.status !== "REVERSED")
        .reduce((total, item) => total + Number(item.amount || 0), 0),
    };
  }, [data]);

  const table = () => {
    if (section === "billing") {
      return (
        <table>
          <thead><tr>
            <th>Bill</th><th>Policy</th><th>Date</th><th>Salesperson</th>
            <th>Amount</th><th>Allocated</th><th>Balance</th><th>Status</th><th />
          </tr></thead>
          <tbody>{rows.map((item) => (
            <tr key={item.id}>
              <td>{item.code}</td><td>{item.policy_code}</td>
              <td>{item.bill_date}</td><td>{item.salesperson}</td>
              <td>{money(item.bill_amount)}</td>
              <td>{money(item.allocated_amount)}</td>
              <td>{money(item.balance)}</td><td>{item.status}</td>
              <td>
                <button
                  className="finance-table-action"
                  onClick={() =>
                    window.location.assign(
                      `/policy-payments/${item.policy_id}`
                    )
                  }
                >Receive</button>
                <button
                  className="finance-table-action"
                  onClick={() => openCreditNote(item)}
                >Credit</button>
              </td>
            </tr>
          ))}</tbody>
        </table>
      );
    }

    if (section === "receipts") {
      return (
        <table>
          <thead><tr>
            <th>Receipt</th><th>Policy</th><th>Reference</th><th>Date</th>
            <th>Tender</th><th>Amount</th><th>Status</th><th />
          </tr></thead>
          <tbody>{rows.map((item) => (
            <tr key={item.id}>
              <td>{item.code}</td><td>{item.policy_code}</td>
              <td>{item.reference || "—"}</td><td>{item.payment_date}</td>
              <td>{item.payment_type}</td><td>{money(item.amount)}</td>
              <td>{item.status}</td>
              <td>{item.status !== "REVERSED" && (
                <button
                  className="finance-table-action danger"
                  onClick={() => reverseReceipt(item)}
                >Reverse</button>
              )}</td>
            </tr>
          ))}</tbody>
        </table>
      );
    }

    if (section === "credit_notes") {
      return (
        <table>
          <thead><tr>
            <th>Credit note</th><th>Policy</th><th>Bill</th><th>Date</th>
            <th>Amount</th><th>Reason</th><th>Status</th>
          </tr></thead>
          <tbody>{rows.map((item) => (
            <tr key={item.id}>
              <td>{item.code}</td><td>{item.policy_code}</td>
              <td>{item.bill_code}</td><td>{item.credit_date}</td>
              <td>{money(item.amount)}</td><td>{item.reason}</td>
              <td>{item.status}</td>
            </tr>
          ))}</tbody>
        </table>
      );
    }

    if (section === "payment_reversals") {
      return (
        <table>
          <thead><tr>
            <th>Reversal</th><th>Receipt</th><th>Policy</th>
            <th>Date</th><th>Amount</th><th>Reason</th><th>Reversed by</th>
          </tr></thead>
          <tbody>{rows.map((item) => (
            <tr key={item.id}>
              <td>{item.code}</td><td>{item.receipt_code}</td>
              <td>{item.policy_code}</td><td>{item.reversal_date}</td>
              <td>{money(item.amount)}</td><td>{item.reason}</td>
              <td>{item.reversed_by}</td>
            </tr>
          ))}</tbody>
        </table>
      );
    }

    if (section === "commission_payments") {
      return (
        <table>
          <thead><tr>
            <th>Payment</th><th>Date</th><th>Salesperson</th>
            <th>Method</th><th>Reference</th><th>Amount</th><th>Status</th><th />
          </tr></thead>
          <tbody>{rows.map((item) => (
            <tr key={item.id}>
              <td>{item.code}</td><td>{item.payment_date}</td>
              <td>{item.sales_person_code} · {item.sales_person}</td>
              <td>{item.payment_method}</td><td>{item.reference || "—"}</td>
              <td>{money(item.amount)}</td><td>{item.status}</td>
              <td>{item.status !== "REVERSED" && (
                <button
                  className="finance-table-action danger"
                  onClick={() => reverseCommission(item)}
                >Reverse</button>
              )}</td>
            </tr>
          ))}</tbody>
        </table>
      );
    }

    if (section === "commission_reversals") {
      return (
        <table>
          <thead><tr>
            <th>Reversal</th><th>Commission payment</th><th>Date</th>
            <th>Salesperson</th><th>Amount</th><th>Reason</th><th>Reversed by</th>
          </tr></thead>
          <tbody>{rows.map((item) => (
            <tr key={item.id}>
              <td>{item.code}</td><td>{item.payment_code}</td>
              <td>{item.reversal_date}</td><td>{item.sales_person}</td>
              <td>{money(item.amount)}</td><td>{item.reason}</td>
              <td>{item.reversed_by}</td>
            </tr>
          ))}</tbody>
        </table>
      );
    }

    return (
      <div className="finance-payer-list">
        {rows.map((payer) => (
          <article key={payer.id}>
            <header>
              <div>
                <strong>{payer.code} · {payer.name}</strong>
                <span>{payer.cellphone || "NO CELLPHONE"} · {payer.email || "NO EMAIL"}</span>
              </div>
              <button onClick={() => openPayer(payer)}>Edit payer</button>
            </header>
            <div className="finance-payer-bank">
              <span>Bank: {payer.bank_name || "—"}</span>
              <span>Branch: {payer.bank_branch || "—"}</span>
              <span>Account: {payer.account_number || "—"}</span>
            </div>
            <div className="finance-payer-policies">
              {payer.policies.length ? payer.policies.map((link) => (
                <span key={link.link_id}>
                  {link.policy_code}
                  {link.collection_amount
                    ? ` · ${money(link.collection_amount)}`
                    : ""}
                </span>
              )) : <span>No linked policies</span>}
            </div>
          </article>
        ))}
      </div>
    );
  };

  return (
    <section className="finance-page">
      <header className="finance-title">
        <div>
          <span>ASSURANCE FINANCE</span>
          <h1>Finance & Transactions</h1>
          <p>
            Bills, receipts, adjustments, reversals, commissions and debit orders.
          </p>
        </div>
        {section === "debit_order_payers" && (
          <button onClick={() => openPayer()}>
            <i className="bi bi-plus-lg" /> New payer
          </button>
        )}
      </header>

      <div className="finance-metrics">
        <article><span>Outstanding bills</span><strong>{money(metrics.billBalance)}</strong></article>
        <article><span>Effective receipts</span><strong>{money(metrics.receipts)}</strong></article>
        <article><span>Effective credits</span><strong>{money(metrics.credits)}</strong></article>
      </div>

      <nav className="finance-sections">
        {sections.map(([key, label, icon]) => (
          <button
            key={key}
            className={section === key ? "active" : ""}
            onClick={() => setSection(key)}
          >
            <i className={`bi ${icon}`} />
            <span>{label}</span>
          </button>
        ))}
      </nav>

      <div className="finance-search">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && load()}
          placeholder="Search the selected finance register"
        />
        <button onClick={load}>Search</button>
      </div>

      <div className="finance-register">
        {rows.length ? table() : (
          <div className="finance-empty">
            No records are available in this register.
          </div>
        )}
      </div>

      {modal === "credit-note" && (
        <div className="finance-modal">
          <form onSubmit={submitCreditNote}>
            <header>
              <div>
                <span>CREDIT NOTE</span>
                <h2>{selected.code} · {selected.policy_code}</h2>
                <p>Reduce the selected outstanding bill without deleting it.</p>
              </div>
              <button type="button" onClick={() => setModal(null)}>×</button>
            </header>
            <div className="finance-form-grid">
              <label>
                Credit date
                <input
                  type="date"
                  required
                  value={form.credit_date}
                  onChange={(event) =>
                    setForm({ ...form, credit_date: event.target.value })
                  }
                />
              </label>
              <label>
                Amount
                <input
                  type="number"
                  min="0.01"
                  max={selected.balance}
                  step="0.01"
                  required
                  value={form.amount}
                  onChange={(event) =>
                    setForm({ ...form, amount: event.target.value })
                  }
                />
              </label>
              <label className="wide">
                Exact reason
                <textarea
                  rows="3"
                  required
                  value={form.reason}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      reason: event.target.value.toUpperCase(),
                    })
                  }
                />
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setModal(null)}>Cancel</button>
              <button className="primary" disabled={busy}>
                {busy ? "Processing..." : "Effect credit note"}
              </button>
            </footer>
          </form>
        </div>
      )}

      {modal === "payer" && (
        <div className="finance-modal">
          <form onSubmit={savePayer}>
            <header>
              <div>
                <span>DEBIT ORDER PAYER</span>
                <h2>{selected ? `Edit ${selected.code}` : "Create payer"}</h2>
                <p>Maintain the payer identity and bank collection details.</p>
              </div>
              <button type="button" onClick={() => setModal(null)}>×</button>
            </header>
            <div className="finance-form-grid">
              {[
                ["name", "Payer name"],
                ["cellphone", "Cellphone"],
                ["email", "Email"],
                ["national_id", "National ID"],
                ["bank_name", "Bank"],
                ["bank_branch", "Bank branch"],
                ["account_number", "Account number"],
              ].map(([field, label]) => (
                <label key={field}>
                  {label}
                  <input
                    type={field === "email" ? "email" : "text"}
                    required={field === "name"}
                    value={form[field] || ""}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        [field]:
                          field === "email"
                            ? event.target.value.toLowerCase()
                            : event.target.value.toUpperCase(),
                      })
                    }
                  />
                </label>
              ))}
            </div>
            <footer>
              <button type="button" onClick={() => setModal(null)}>Cancel</button>
              <button className="primary" disabled={busy}>
                {busy ? "Saving..." : "Save payer"}
              </button>
            </footer>
          </form>
        </div>
      )}
    </section>
  );
}

export default FinanceWorkspace;
